import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
import { ONE_TIME_PACKAGES } from '../data/constants';

const galleryPhotos = [
  { src: '/images/raffle-interior-front-seat.jpg', label: 'Front Seat Detail' },
  { src: '/images/raffle-interior-console.jpg', label: 'Dash & Console' },
  { src: '/images/raffle-interior-floorboards.jpg', label: 'Spotless Floorboards' },
  { src: '/images/raffle-interior-rear-seat.jpg', label: 'Rear Seat Detail' },
  { src: '/images/raffle-interior-mat-keys.jpg', label: 'Ready to Go' },
  { src: '/images/raffle-interior-trunk.jpg', label: 'Trunk & Cargo' },
];

const processSteps = [
  { number: '01', title: 'Blowout & Vacuum', subtitle: 'Clearing the Canvas' },
  { number: '02', title: 'Steam Clean & Wipe Down', subtitle: 'Deep Sanitization' },
  { number: '03', title: 'Shampoo & Extraction', subtitle: "Lifting What's Embedded" },
  { number: '04', title: 'Final Touches', subtitle: 'The Details That Matter' },
];

const stats = [
  { stat: '70%', desc: 'reduction in headlight output from oxidized lenses' },
  { stat: '99.9%', desc: 'of bacteria and viruses killed by steam at 212°F' },
  { stat: '$4,000', desc: 'premium a well-maintained car commands at trade-in' },
];

const reviews = [
  { text: "Akeem transformed my car from a daily driver into something that looks showroom fresh. The attention to detail is incredible!", name: "Sarah M., St. Mary's County" },
  { text: "The Monarch Maintenance Program is worth every penny. My SUV has never looked better, and the monthly perks are amazing!", name: "Michael T., Charles County" },
  { text: "I've tried other detailers, but none compare to Akeem's work. He truly treats your car like it's his own.", name: "Jennifer L., Calvert County" },
  { text: "The convenience of having Akeem come to my workplace is a game-changer. Professional service with exceptional results!", name: "David K., St. Mary's County" },
];

export default function HomePage() {
  const navigate = useNavigate();
  const oneTimePackages = ONE_TIME_PACKAGES.filter(pkg => pkg.id !== 'dev_test');

  return (
    <div className="min-h-screen bg-[#F5F5F0] text-[#111827]">

      {/* Hero */}
      <section className="relative h-[85vh] min-h-[560px] overflow-hidden flex items-end">
        <img
          src="/images/homepage-hero.jpg"
          alt="Monarch Detailing — recent interior work"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        <div className="relative z-10 w-full px-6 pb-14">
          <div className="max-w-3xl mx-auto text-center text-white">
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Interior Detailing · Southern Maryland</p>
            <h1 className="text-4xl md:text-6xl font-bold mb-5 leading-tight">
              Your Car Should<br /><span className="text-[#68caf1]">Feel New Again.</span>
            </h1>
            <p className="text-base md:text-lg text-gray-200 max-w-xl mx-auto leading-relaxed mb-8">
              Dust, stains, and odors build up over time — and most people get used to it. We come to you, remove all of it, and hand your car back smelling and feeling like it just left the lot.
            </p>
            <button
              onClick={() => navigate('/detail')}
              className="inline-flex items-center gap-2 bg-[#68caf1] text-black font-bold px-8 py-4 rounded-2xl hover:bg-[#5bb8db] transition-all active:scale-95"
            >
              Book Your Detail <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Proof strip */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest text-center mb-8">Recent Work</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
            {galleryPhotos.map(({ src, label }) => (
              <div key={src} className="relative rounded-xl overflow-hidden aspect-square group">
                <img src={src} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <p className="absolute bottom-3 left-3 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">{label}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => navigate('/transformations')} className="text-sm font-bold text-[#68caf1] hover:underline underline-offset-2 inline-flex items-center gap-1">
              See All Transformations <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest text-center mb-3">The Alchemist Process</p>
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6 mb-10">
            {processSteps.map(step => (
              <div key={step.number} className="text-center md:text-left">
                <p className="text-[#68caf1] text-3xl font-bold mb-2">{step.number}</p>
                <p className="font-bold text-[#111827] mb-1">{step.title}</p>
                <p className="text-[#6B7280] text-sm">{step.subtitle}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => navigate('/process')} className="text-sm font-bold text-[#68caf1] hover:underline underline-offset-2 inline-flex items-center gap-1">
              See the Full Process <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest mb-4">Why "Monarch"</p>
            <h2 className="text-3xl font-bold mb-5">Not a car wash.<br />A calling.</h2>
            <p className="text-[#6B7280] leading-relaxed mb-4">
              The monarch butterfly doesn't just change — it completely transforms. That's the standard I hold myself to with every vehicle. In 2017, I lost my sister Akeema James in a car accident. Monarch Detailing is dedicated to her memory — her name lives on my license plate, in this business, and in every car I touch.
            </p>
            <p className="text-[#6B7280] leading-relaxed">
              A clean car isn't just about appearance. It's about safety, presence, and love — my way of carrying her forward.
            </p>
            <button onClick={() => navigate('/about')} className="mt-6 text-sm font-bold text-[#68caf1] hover:underline underline-offset-2 inline-flex items-center gap-1">
              Read the Full Story <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <img
            src="/images/5975162c-f25b-435c-bcda-ef7227fbd3ae.jpg"
            alt="Akeem James, founder of Monarch Detailing"
            className="rounded-2xl w-full h-80 object-cover"
          />
        </div>
      </section>

      {/* Packages */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest text-center mb-3">Pick Your Path</p>
          <h2 className="text-3xl font-bold text-center mb-10">Packages</h2>
          <div className="flex flex-col gap-5 mb-6">
            {oneTimePackages.map(pkg => (
            <button
              key={pkg.id}
              onClick={() => navigate(`/detail/book?package=${pkg.id}`)}
              className={`group bg-[#F5F5F0] border-2 ${pkg.highlight ? 'border-[#68caf1]' : 'border-gray-200'} rounded-2xl p-8 flex flex-col text-left hover:shadow-xl transition-all duration-200 touch-manipulation active:scale-[0.99]`}
            >
              <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-4">One-Time Detail · Interior Package</p>
              <div className="flex items-end gap-3 mb-3">
                <p className="font-bold text-[#111827] text-3xl leading-none">{pkg.emoji} {pkg.name}</p>
                <p className="text-[#68caf1] font-bold text-lg leading-none mb-0.5">from ${pkg.price}</p>
              </div>
              <p className="text-[#111827] text-sm font-semibold leading-relaxed mb-1">{pkg.tagline}</p>
              <p className="text-[#6B7280] text-sm leading-relaxed mb-2">Sedan ${pkg.price} · SUV ${pkg.suvPrice} · Truck ${pkg.truckPrice}</p>
              <div className="border-t border-gray-200 my-4 w-full" />
              <ul className="space-y-2 mb-4">
                {pkg.features.map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-[#374151]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#68caf1] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-widest mb-2">Not included:</p>
                  <ul className="space-y-2">
                    {pkg.notIncluded.map(item => (
                      <li key={item} className="flex items-center gap-2 text-sm text-[#6B7280]">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#111827] group-hover:gap-3 transition-all">
                Book {pkg.name} <ArrowRight className="w-4 h-4" />
              </span>
            </button>
            ))}

            <button
              onClick={() => navigate('/maintenance')}
              className="group bg-[#111827] border-2 border-[#111827] rounded-2xl p-8 flex flex-col text-left hover:border-[#68caf1] transition-all duration-200 touch-manipulation active:scale-[0.99]"
            >
              <p className="text-xs font-bold text-[#68caf1] uppercase tracking-widest mb-4">Monthly Program</p>
              <div className="flex items-end gap-3 mb-3">
                <p className="font-bold text-white text-3xl leading-none">Interior Maintenance</p>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-2">Reset from $200 · then $75–$85/month after</p>
              <div className="border-t border-white/10 my-4" />
              <ul className="space-y-2 mb-6">
                {[
                  'Alchemist Reset brings your car to pristine — then we keep it there',
                  'Steam & deep shampoo included in base — add-ons for pet hair, odor & stains',
                  'Monthly visits from $75 — flat rate, no condition charges',
                  'Sedan $75 · SUV $80 · Truck $85 per month',
                  '50% deposit today, balance at service',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2 text-sm text-gray-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#68caf1] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#68caf1] group-hover:gap-3 transition-all">
                See the Program <ArrowRight className="w-4 h-4" />
              </span>
            </button>
          </div>


          {/* Referral */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl py-3 px-5 flex items-center gap-3">
            <Star className="w-4 h-4 text-amber-500 shrink-0" />
            <p className="text-sm text-[#111827]">
              <span className="font-bold">Refer a friend</span>
              <span className="text-[#6B7280]"> — they get $15 off their first detail. You get $15 off your next one.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Why it matters — stats */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[#68caf1] text-xs font-bold uppercase tracking-widest text-center mb-3">Why It Matters</p>
          <h2 className="text-3xl font-bold text-center mb-12">More Than Just Clean</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {stats.map(s => (
              <div key={s.desc} className="text-center">
                <p className="text-4xl font-bold text-[#68caf1] mb-2">{s.stat}</p>
                <p className="text-[#6B7280] text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={() => navigate('/education')} className="text-sm font-bold text-[#68caf1] hover:underline underline-offset-2 inline-flex items-center gap-1">
              Learn More <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">What Our Clients Say</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {reviews.map(r => (
              <div key={r.name} className="bg-[#F5F5F0] rounded-2xl p-6">
                <p className="text-[#374151] text-sm leading-relaxed mb-4">"{r.text}"</p>
                <p className="text-[#68caf1] text-sm font-bold">{r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quiz CTA */}
      <section className="px-6 py-10 max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/interior-check')}
          className="w-full bg-[#F5F5F0] border border-gray-200 rounded-2xl py-5 px-6 flex items-center justify-between hover:border-[#68caf1] transition-all touch-manipulation group"
        >
          <div className="text-left">
            <p className="font-bold text-[#111827] text-sm">Not sure what you need?</p>
            <p className="text-[#6B7280] text-xs mt-0.5">Answer 5 questions and get a free interior score — A through F</p>
          </div>
          <span className="text-[#68caf1] font-bold text-sm whitespace-nowrap ml-4 group-hover:underline underline-offset-2">
            Get My Score →
          </span>
        </button>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-6 bg-[#111827] text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to feel the difference?</h2>
          <p className="text-gray-400 mb-8">We come to you, anywhere in St. Mary's, Charles & Calvert County, MD.</p>
          <button
            onClick={() => navigate('/detail')}
            className="inline-flex items-center gap-2 bg-[#68caf1] text-black font-bold px-8 py-4 rounded-2xl hover:bg-[#5bb8db] transition-all active:scale-95"
          >
            Book Your Detail <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-gray-500 text-xs mt-8">Monarch Detailing · (301) 885-9767 · St. Mary's, Charles &amp; Calvert County, MD</p>
        </div>
      </section>

    </div>
  );
}
